/* ==========================================================================
   Showroom landing pages (v2 / v3 / v4)
   --------------------------------------------------------------------------
   Four jobs, all optional and independently guarded so a missing section
   never breaks the rest of the page:

   1. Video tour  — custom play overlay over the native <video>.
   2. Google reviews — same proxy the rest of the site uses
                       (see snippets/google-reviews-cara.liquid).
   3. Booking form — the HubSpot embed. Loads HubSpot's forms script, renders
                     the form, and restyles it inside HubSpot's iframe to
                     match the page. Swaps in call / email copy if HubSpot is
                     blocked or never arrives.
   4. Meta Lead    — one Lead to pixel 794214226873441 per successful HubSpot
                     submission, only with marketing consent. The base pixel
                     code is in layout/theme.liquid.
   ========================================================================== */

(function () {
  'use strict';

  var REVIEWS_ENDPOINT = 'https://google-reviews-proxy-kappa.vercel.app/api/get-reviews';
  var TRUNCATE_AT = 180;

  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  /* ------------------------------------------------------------------------
     1. Video tour
     ------------------------------------------------------------------------ */

  function initVideo(wrap) {
    var video = wrap.querySelector('video');
    var button = wrap.querySelector('.sr-play');
    if (!video || !button) return;

    function markPlaying() {
      wrap.classList.add('is-playing');
    }

    button.addEventListener('click', function () {
      var played = video.play();
      if (played && played.catch) played.catch(function () {});
      markPlaying();
    });

    video.addEventListener('play', markPlaying);
  }

  /* ------------------------------------------------------------------------
     2. Google reviews
     ------------------------------------------------------------------------ */

  function starString(rating) {
    var n = Math.max(0, Math.min(5, Math.round(rating || 5)));
    return '★★★★★'.slice(0, n) + '☆☆☆☆☆'.slice(0, 5 - n);
  }

  function buildReviewCard(review) {
    var card = document.createElement('div');
    card.style.cssText =
      'background: #fff; border-radius: 8px; padding: clamp(20px, 3vw, 28px);';

    var stars = document.createElement('p');
    stars.style.cssText =
      'margin: 0 0 12px; font-size: 15px; letter-spacing: 0.1em; color: #6f4d1f;';
    stars.textContent = review.stars;
    card.appendChild(stars);

    var body = document.createElement('p');
    body.style.cssText =
      'margin: 0 0 10px; font-size: 16px; line-height: 1.65; color: rgba(43,44,45,0.85);';
    body.textContent = review.short;
    card.appendChild(body);

    if (review.full !== review.short) {
      var wrap = document.createElement('p');
      wrap.style.cssText = 'margin: 0 0 14px;';

      var toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'sr-review-more';
      toggle.textContent = 'Read more';

      var expanded = false;
      toggle.addEventListener('click', function () {
        expanded = !expanded;
        body.textContent = expanded ? review.full : review.short;
        toggle.textContent = expanded ? 'Show less' : 'Read more';
      });

      wrap.appendChild(toggle);
      card.appendChild(wrap);
    }

    var author = document.createElement('p');
    author.style.cssText = 'margin: 0; font-size: 14px; font-weight: 700;';
    author.textContent = review.author;
    card.appendChild(author);

    return card;
  }

  function renderReviews(data) {
    var result = (data && data.result) || {};
    var rating = result.rating ? parseFloat(result.rating).toFixed(1) : null;
    var count = result.user_ratings_total || null;

    // Hero strapline, and the heading above the review cards.
    if (rating && count) {
      each('[data-sr-hero-rating]', function (el) {
        el.textContent = 'Rated ' + rating + ' by ' + count + ' local businesses on Google';
      });
      each('[data-sr-rating-line]', function (el) {
        el.textContent = rating + ' / 5.0 from ' + count + ' reviews';
      });
    }

    var reviews = (result.reviews || [])
      .slice(0, 3)
      .map(function (review) {
        var text = (review.text || '').trim();
        return {
          stars: starString(review.rating),
          full: text,
          short: text.length > TRUNCATE_AT ? text.slice(0, TRUNCATE_AT).trim() + '…' : text,
          author: review.author_name || ''
        };
      })
      .filter(function (review) {
        return review.full;
      });

    if (!reviews.length) return;

    each('[data-sr-review-list]', function (list) {
      list.textContent = '';
      reviews.forEach(function (review) {
        list.appendChild(buildReviewCard(review));
      });

      var section = list.closest('.sr-reviews');
      if (section) section.hidden = false;
    });
  }

  function loadReviews() {
    if (!document.querySelector('[data-sr-review-list], [data-sr-hero-rating]')) return;

    fetch(REVIEWS_ENDPOINT)
      .then(function (response) {
        return response.json();
      })
      .then(renderReviews)
      .catch(function () {
        /* Hero falls back to its hard-coded line, review section stays hidden. */
      });
  }

  /* ------------------------------------------------------------------------
     3. Booking form — HubSpot embed
     ------------------------------------------------------------------------ */

  // HubSpot draws the form inside a same-origin iframe, so the page's CSS never
  // reaches the fields. These rules are injected into that iframe once the form
  // is ready. __FONT__ becomes the theme's body font family.
  var HUBSPOT_CSS = [
    'html, body { background: transparent !important; }',
    'body, .hs-form, .hs-form *, .submitted-message { font-family: __FONT__ !important; }',
    '.hs-form fieldset { max-width: none !important; }',
    '.hs-form .hs-form-field { box-sizing: border-box !important; margin: 0 0 18px !important; }',
    '.hs-form .hs-form-field .input { margin-right: 0 !important; }',
    // Side-by-side first / last name. Mirrors the breakpoint where HubSpot's own
    // CSS stacks the columns, so the gutter never applies to stacked fields.
    '@media (min-width: 401px) and (min-device-width: 481px) {',
    '  .hs-form fieldset.form-columns-2 .hs-form-field:first-child { padding-right: 9px !important; }',
    '  .hs-form fieldset.form-columns-2 .hs-form-field:last-child { padding-left: 9px !important; }',
    '}',
    '.hs-form .hs-form-field > label { display: block !important; margin: 0 0 7px !important; font-size: 14px !important; font-weight: 500 !important; color: #2b2c2d !important; }',
    '.hs-form .hs-form-required { margin-left: 2px !important; color: #c0392b !important; }',
    '.hs-form .hs-input:not([type=checkbox]):not([type=radio]) { box-sizing: border-box !important; width: 100% !important; height: 50px !important; padding: 0 14px !important; border: 1px solid rgba(43,44,45,0.35) !important; border-radius: 6px !important; background: #fff !important; font-size: 16px !important; color: #2b2c2d !important; box-shadow: none !important; }',
    '.hs-form textarea.hs-input { height: auto !important; min-height: 104px !important; padding: 13px 14px !important; resize: vertical !important; }',
    '.hs-form .hs-input:focus { border-color: #c0392b !important; outline: 2px solid rgba(192,57,43,0.35) !important; }',
    '.hs-form .hs-input.error, .hs-form .hs-input.invalid { border-color: #c0392b !important; }',
    '.hs-form .hs-error-msgs { margin: 6px 0 0 !important; padding: 0 !important; list-style: none !important; }',
    '.hs-form .hs-error-msgs label, .hs-form .hs-error-msg, .hs-form .hs_error_rollup label { margin: 0 !important; font-size: 13px !important; font-weight: 500 !important; color: #c0392b !important; }',
    '.hs-form .actions { margin: 4px 0 0 !important; padding: 0 !important; }',
    '.hs-form .hs-button { box-sizing: border-box !important; width: 100% !important; min-height: 56px !important; padding: 0 24px !important; border: 0 !important; border-radius: 6px !important; background: #c0392b !important; color: #fff !important; font-size: 17px !important; font-weight: 700 !important; line-height: 1.2 !important; box-shadow: none !important; text-shadow: none !important; cursor: pointer; transition: background-color 0.15s ease; }',
    '.hs-form .hs-button:hover, .hs-form .hs-button:focus-visible { background: #a93226 !important; }',
    '.submitted-message { font-size: 17px !important; line-height: 1.65 !important; color: #2b2c2d !important; }'
  ].join('\n');

  // The theme's fonts are @font-face rules in an inline <style> in theme.liquid.
  // The iframe can't see them and would fall back to Helvetica, so copy across
  // the rules for whatever the body font currently is.
  function themeFont(el) {
    var family = window.getComputedStyle(el).fontFamily || 'sans-serif';
    var primary = family.split(',')[0].replace(/["']/g, '').trim().toLowerCase();
    var faces = [];

    Array.prototype.forEach.call(document.querySelectorAll('style'), function (style) {
      (style.textContent.match(/@font-face\s*\{[^}]*\}/g) || []).forEach(function (rule) {
        if (primary && rule.toLowerCase().indexOf(primary) !== -1) faces.push(rule);
      });
    });

    return { family: family, faces: faces.join('\n') };
  }

  // HubSpot sizes the iframe itself, but measures before these styles settle
  // and comes up short (by 18px on the live form), clipping the button. Hold a
  // min-height at the real content height. min-height rather than height, so
  // this never fights HubSpot's own resizing.
  function syncFrameHeight(frame, doc) {
    var bottom = 0;
    Array.prototype.forEach.call(doc.body.children, function (child) {
      bottom = Math.max(bottom, child.getBoundingClientRect().bottom);
    });
    var margin = parseFloat(doc.defaultView.getComputedStyle(doc.body).marginBottom) || 0;
    frame.style.minHeight = Math.ceil(bottom + margin) + 'px';
  }

  function styleHubspotFrame(target) {
    var frame = target.querySelector('iframe');
    if (!frame) return;

    var doc;
    try {
      doc = frame.contentDocument;
    } catch (error) {
      return; // Cross-origin after all: leave HubSpot's own styling in place.
    }
    if (!doc || !doc.head || !doc.body) return;

    if (!doc.getElementById('sr-hubspot-style')) {
      var font = themeFont(target);
      var style = doc.createElement('style');
      style.id = 'sr-hubspot-style';
      style.textContent = font.faces + '\n' + HUBSPOT_CSS.replace('__FONT__', font.family);
      doc.head.appendChild(style);
    }

    var sync = function () {
      syncFrameHeight(frame, doc);
    };
    sync();
    if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(sync);

    // HubSpot can rebuild the iframe's body (validation errors, the thank-you
    // message), so re-attach the observer whenever the body changes.
    if (frame.srObservedBody !== doc.body && frame.contentWindow && frame.contentWindow.ResizeObserver) {
      if (frame.srResizeObserver) frame.srResizeObserver.disconnect();
      frame.srResizeObserver = new frame.contentWindow.ResizeObserver(sync);
      frame.srResizeObserver.observe(doc.body);
      frame.srObservedBody = doc.body;
    }
  }

  var hubspotLoading = null;

  function loadHubspot(region) {
    if (window.hbspt && window.hbspt.forms) return Promise.resolve();

    if (!hubspotLoading) {
      hubspotLoading = new Promise(function (resolve, reject) {
        var host = !region || region === 'na1' ? 'js.hsforms.net' : 'js-' + region + '.hsforms.net';
        var script = document.createElement('script');
        script.src = 'https://' + host + '/forms/embed/v2.js';
        script.charset = 'utf-8';
        script.async = true;
        script.onload = function () {
          if (window.hbspt && window.hbspt.forms) {
            resolve();
          } else {
            reject(new Error('HubSpot forms script loaded without hbspt.forms'));
          }
        };
        script.onerror = function () {
          hubspotLoading = null;
          reject(new Error('HubSpot forms script failed to load'));
        };
        document.head.appendChild(script);
      });
    }

    return hubspotLoading;
  }

  function initHubspot(target) {
    if (target.hasAttribute('data-sr-hubspot-started')) return;
    target.setAttribute('data-sr-hubspot-started', '');

    var card = target.parentElement;
    var status = card.querySelector('[data-sr-hubspot-status]');
    var fallback = card.querySelector('[data-sr-hubspot-fallback]');
    var region = target.getAttribute('data-region') || 'eu1';
    var trackLead = createLeadTracker(window);

    var show = function (el, visible) {
      if (el) el.hidden = !visible;
    };
    var fail = function () {
      show(status, false);
      show(fallback, true);
    };

    show(status, true);

    // Ad blockers can stall HubSpot without firing an error, so don't wait forever.
    var timer = window.setTimeout(fail, 15000);

    loadHubspot(region)
      .then(function () {
        var options = {
          region: region,
          portalId: target.getAttribute('data-portal-id'),
          formId: target.getAttribute('data-form-id'),
          target: '#' + target.id,
          onFormReady: function () {
            // Also runs if the form turns up after the fallback was shown.
            window.clearTimeout(timer);
            show(status, false);
            show(fallback, false);
            styleHubspotFrame(target);
          },
          onFormSubmitted: function () {
            // HubSpot's post-submission callback. It fires once HubSpot has
            // accepted the submission (the event carries the server's
            // conversion ID), not on the button click — that's onFormSubmit.
            trackLead();

            // HubSpot swaps the form for its thank-you message.
            window.setTimeout(function () {
              styleHubspotFrame(target);
            }, 50);
          }
        };

        var submitText = target.getAttribute('data-submit-text');
        if (submitText) options.submitText = submitText;

        window.hbspt.forms.create(options);
      })
      .catch(function () {
        window.clearTimeout(timer);
        fail();
      });
  }

  /* ------------------------------------------------------------------------
     4. Meta Lead event
     ------------------------------------------------------------------------ */

  // The same pixel the base code in layout/theme.liquid loads.
  var META_PIXEL_ID = '794214226873441';

  // Shopify's cookie banner is the source of truth for consent on this site.
  function marketingConsentGiven(win) {
    var privacy = win.Shopify && win.Shopify.customerPrivacy;
    return !!(privacy && typeof privacy.marketingAllowed === 'function' && privacy.marketingAllowed());
  }

  // One tracker per rendered form, called from HubSpot's onFormSubmitted. A
  // rendered form can only produce one lead (HubSpot replaces it with the
  // thank-you message), so the first call claims the submission and any repeat
  // callback is ignored. Consent is read at that moment: a lead submitted
  // without consent is never sent, even if consent arrives afterwards.
  // trackSingle rather than track, so the Lead reaches only this pixel and not
  // the other Meta pixels on the page (see theme.liquid).
  function createLeadTracker(win) {
    var claimed = false;

    return function trackLead() {
      if (claimed) return false;
      claimed = true;

      if (!marketingConsentGiven(win) || typeof win.fbq !== 'function') return false;

      win.fbq('trackSingle', META_PIXEL_ID, 'Lead', { content_name: 'Showroom visit enquiry' });
      return true;
    };
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */

  function each(selector, fn) {
    Array.prototype.forEach.call(document.querySelectorAll(selector), fn);
  }

  function init() {
    each('.sr-video-wrap', initVideo);
    each('[data-sr-hubspot]', initHubspot);
    loadReviews();
  }

  // Under node (tests/showroom-lp.test.js): expose the pure helpers, skip the DOM.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { createLeadTracker: createLeadTracker, META_PIXEL_ID: META_PIXEL_ID };
  }
  if (typeof document === 'undefined') return;

  ready(init);

  // Re-run when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
